import { useCallback } from 'react';
import { useRxCollection, useRxData } from 'rxdb-hooks';

import { useMst } from '../../mobx-models/Root';
import { DeficiencyComponentRemovalPayload } from '../../models/models';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { RxdbCollectionName } from '../rxdbCollectionName';
import type {
  DeficiencyComponentRemoval,
  DeficiencyComponentRemovalCollection,
} from './deficiencyComponentRemoval.types';

const useDeficiencyComponentRemoval = (assetId: number) => {
  const { SiteDiscipline } = useMst();
  const deficiencyComponentRemovalCollection: DeficiencyComponentRemovalCollection =
    useRxCollection(RxdbCollectionName.DEFICIENCY_COMPONENT_REMOVAL);

  const getDeficiencyComponentRemovalQuery = useCallback(
    (collection: DeficiencyComponentRemovalCollection) =>
      collection?.find({
        selector: {
          $and: [{ assetId }],
        },
      }),
    [assetId],
  );

  const { result: deficiencyComponentRemoval } = useRxData<DeficiencyComponentRemoval>(
    RxdbCollectionName.DEFICIENCY_COMPONENT_REMOVAL,
    getDeficiencyComponentRemovalQuery,
    { json: true },
  );

  const saveDeficiencyComponentRemoval = async (payload: DeficiencyComponentRemovalPayload) => {
    const removalPayload: DeficiencyComponentRemovalPayload = {
      ...payload,
      siteId: SiteDiscipline.siteId,
      disciplineId: SiteDiscipline.disciplineId,
    };
    try {
      delete removalPayload.isChanged;
      if (removalPayload.componentVersionUUId !== '') {
        let result = await deficiencyComponentRemovalCollection
          ?.findOne({
            selector: {
              componentVersionUUId: removalPayload.componentVersionUUId,
            },
          })
          .update({
            $set: {
              comment: removalPayload.comment,
              componentRemovalId: removalPayload.componentRemovalId,
              parentComponentId: removalPayload.parentComponentId,
              parentComponentName: removalPayload.parentComponentName,
              isActive: removalPayload.isActive === null ? true : removalPayload.isActive,
              componentRemovalIdBySC: removalPayload.componentRemovalIdBySC,
              commentBySC: removalPayload.commentBySC,
              inspectionId: removalPayload.inspectionId,
              componentVersionId: removalPayload.componentVersionId,
              componentVersionInspectionId: removalPayload.componentVersionInspectionId,
              componentVersionUUId: removalPayload.componentVersionUUId,
              deficiencyUUID: removalPayload.deficiencyUUID,
              siteId: SiteDiscipline.siteId,
              disciplineId: SiteDiscipline.disciplineId,
              deficiencyId: removalPayload.deficiencyId,
            },
          });
        if (result === null) {
          result = await deficiencyComponentRemovalCollection?.insert(removalPayload);
        }
      }
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useRiskAssessment.tsx ~ line 54 ~ saveDeficiencyComponentRemoval ~',
      );
    }
  };

  return { deficiencyComponentRemoval, saveDeficiencyComponentRemoval };
};

export default useDeficiencyComponentRemoval;
